import React, { ReactNode, useState } from 'react'
import styled from 'styled-components'
import { _, colors } from 'utils'
import Button from '../Button'
import Divider from '../Divider'
import DropdownButton from '../DropdownButton'
import Icon from '../Icon'
import BulkActions, { BulkActionsProps } from './BulkActions'
import DateFilter from './DateFilter'
import Filter, { FilterProps } from './Filter'
import SearchInput from './SearchInput'

const S = {
    FilterIcon: styled(Icon)`
        margin-right: 8px;
    `,
    ToolbarHeader: styled.h1`
        margin: 0;
        font-weight: 500;
        font-size: 18px;
        line-height: 21px;
        color: ${colors.grayTitle};
    `,
    FilterWrapper: styled.div<{ isShow?: boolean }>`
        display: ${({ isShow }) => (isShow ? 'flex' : 'none')};
        align-items: center;
        justify-content: space-between;
        margin: 0 -20px;
        padding: 0 20px;
        background: ${colors.darkOpacity(0.05)};
    `,
    SearchInput: styled(SearchInput)`
        margin: 8px 0 !important;
    `,
    Filter: styled(Filter)`
        width: 197px;
        margin-left: 20px;
    `,
}

interface Props {
    addItemText?: string
    disabledAdd?: boolean
    disabledAddTooltip?: string
    onAddItem?: () => void
    filters?: FilterProps[]
    onSearch?: (e: any) => void
    searchPlaceHolder?: string
    sortOptions?: Record<string, string>[]
    onSort?: (sort?: any) => void
    dateFilter?: {
        format?: string
        onChangeDate: (e?: any) => void
        placeHolder?: string
    }
    renderExtraActions?: (props?: any) => ReactNode
    bulkActions?: BulkActionsProps
    renderAddButton?: (props?: any) => ReactNode
}

const TopToolbar = ({
    addItemText,
    disabledAdd,
    disabledAddTooltip,
    onAddItem,
    filters = [],
    dateFilter: {
        format,
        onChangeDate,
        placeHolder: dateFilterPlaceHolder,
    },
    onSearch = () => null,
    searchPlaceHolder,
    sortOptions = [],
    onSort,
    renderExtraActions,
    bulkActions,
    renderAddButton,
}: Props) => {
    const [isShow, setIsShow] = useState(false)
    const handleChangeFilter = (selectedValue, filter) => {
        if (filter.isMulti) {
            filter.onChange(selectedValue.map((e) => e[filter.optionValue || 'value']))
        } else {
            filter.onChange(selectedValue && selectedValue[filter.optionValue || 'value'])
        }
    }

    return (
        <div>
            <div className="flex-space-between">
                <div className="flex-space-between">
                    <S.FilterIcon
                        icon={isShow ? 'close' : 'filter'}
                        onClick={() => setIsShow(!isShow)}
                    />
                    <S.ToolbarHeader>
                        {_('list.filter')}
                    </S.ToolbarHeader>
                </div>
                <div className="flex-align-center">
                    {renderExtraActions?.()}
                    {bulkActions && <BulkActions {...bulkActions} />}
                    {renderAddButton?.() || (
                        <Button
                            size="sm"
                            icon="plusBox"
                            onClick={onAddItem}
                            disabled={disabledAdd}
                            tooltip={disabledAddTooltip}
                            className="ml-2"
                        >
                            {addItemText}
                        </Button>
                    )}
                </div>
            </div>
            <Divider marginTop={10} marginBottom={0} />
            <S.FilterWrapper isShow={isShow}>
                <div className="flex-space-between">
                    <S.SearchInput
                        lg="280px"
                        placeholder={searchPlaceHolder}
                        onChange={onSearch}
                    />
                    {filters.map((filter, index) => (
                        <S.Filter
                            key={index}
                            placeholder={filter.placeholder}
                            defaultValue={filter.defaultValue}
                            options={filter.options}
                            onChange={(value) => handleChangeFilter(value, filter)}
                            isMulti={filter.isMulti}
                            isClearable={filter.isClearable}
                            optionLabel={filter.optionLabel || 'label'}
                            optionValue={filter.optionValue || 'value'}
                        />
                    ))}
                    {
                        onChangeDate && (
                            <DateFilter
                                onChange={onChangeDate}
                                format={format}
                                placeHolder={dateFilterPlaceHolder}
                            />
                        )
                    }
                </div>
                {sortOptions.length > 0 ? (
                    <DropdownButton name={_('show.list.sort')} variant="light">
                        {sortOptions.map((sort, index) => (
                            <div key={index} onClick={() => onSort(sort.value)}>
                                {sort.label}
                            </div>
                        ))}
                    </DropdownButton>
                ) : <div />}
            </S.FilterWrapper>
            <Divider marginBottom={40} />
        </div>
    )
}

TopToolbar.defaultProps = {
    dateFilter: {}
}

export default TopToolbar
